import { useQuery } from '@apollo/client';
import RequiresAuth from '@website/components/auth/RequiresAuth';
import HostDashboardSettings from '@website/components/hosts/dashboard/HostDashboardSettings';
import { HostMissingSettingsAlerts } from '@website/components/hosts/dashboard/HostMissingSettingsAlerts';
import HostTripCards from '@website/components/hosts/dashboard/HostTripCards';
import ErrorPage from '@website/components/utilities/ErrorPage';
import NotFound from '@website/components/utilities/NotFound';
import { GetHostDashboardDataDocument } from '@website/types/graphqlOperations';
import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { Breadcrumb, Button, Container } from 'react-bootstrap';
import LoadingPage from '../../../../components/utilities/LoadingPage';

interface HostDashboardProps {
  hostURL: string;
}

export function HostDashboard({ hostURL }: HostDashboardProps) {
  const { loading, error, data } = useQuery(GetHostDashboardDataDocument, {
    variables: {
      urlSlug: hostURL,
    },
    fetchPolicy: 'cache-and-network',
  });

  if (loading && !data) {
    return <LoadingPage />;
  }

  // show 404 if host is not found
  if (error && error.message.match(/- not found/i)) {
    return <NotFound />;
  }

  if (error && !data) {
    return <ErrorPage error={error} />;
  }

  return (
    <Container className="pt-3">
      <Breadcrumb>
        <Link href={`/hosts/dashboard/${hostURL}/bookings`} legacyBehavior>
          <Breadcrumb.Item href="">Host Dashboard</Breadcrumb.Item>
        </Link>
        <Breadcrumb.Item active>{data.hostByURL.name}</Breadcrumb.Item>
      </Breadcrumb>

      <h1>{data.hostByURL.name}</h1>
      <HostMissingSettingsAlerts host={data.hostByURL} />
      <div className="d-grid gap-1">
        <Link href={`/hosts/dashboard/${hostURL}/bookings`} legacyBehavior>
          <Button variant="primary" size="lg">
            Bookings
          </Button>
        </Link>

        <Link href={`/hosts/dashboard/${hostURL}/messages`} legacyBehavior>
          <Button variant="primary" size="lg">
            Messages
          </Button>
        </Link>

        <Link href={`/hosts/dashboard/${hostURL}/statements`} legacyBehavior>
          <Button variant="primary" size="lg">
            Payout statements
          </Button>
        </Link>

        <Link href={`/hosts/dashboard/${hostURL}/reviews`} legacyBehavior>
          <Button variant="primary" size="lg">
            Reviews
          </Button>
        </Link>

        <h2 className="mt-4 h2">Settings</h2>
        <HostDashboardSettings host={data.hostByURL} />

        <h2 className="mt-4">Your Trips</h2>
        <HostTripCards trips={data.hostByURL.trips.items} />

        <Link
          href="/trips/add/[urlSlug]"
          as={`/trips/add/${data.hostByURL.id}`}
          passHref
          legacyBehavior
        >
          <Button variant="success" size="lg">
            + Add a trip
          </Button>
        </Link>
      </div>
    </Container>
  );
}

export default function HostDashboardPage({ hostURL }: HostDashboardProps) {
  return (
    <RequiresAuth>
      <HostDashboard hostURL={hostURL} />
    </RequiresAuth>
  );
}

export const getServerSideProps: GetServerSideProps<
  HostDashboardProps
> = async ({ query }) => {
  return {
    props: {
      hostURL: query.hostURL as string,
    },
  };
};
